import React, { useState, useEffect } from "react";
import "../../styles/MyLearning.css";
import { getLearningCourse, getLearningDashboard, updateLearningProgress } from "../../api/learningApi";
import { getCourses } from "../../api/contentApi";
import { courseTitleToSlug } from "../../api/courseSlug";
import YouTubeLecturePlayer from "../learning/YouTubeLecturePlayer";
import ApiContentState from "../common/ApiContentState";

export default function MyLearning({ currentUser, onSelectCourse }) {
  const [activeTab, setActiveTab] = useState("all");

  // Modals & Player State
  const [activeCoursePlayer, setActiveCoursePlayer] = useState(null);
  const [activeLectureIndex, setActiveLectureIndex] = useState(0);
  const [playerTab, setPlayerTab] = useState("resources"); // "resources" | "notes"
  const [activeCertificateCourse, setActiveCertificateCourse] = useState(null);

  // Lesson Notes State (Synchronized with localStorage)
  const [lessonNoteText, setLessonNoteText] = useState("");
  const [savedNotes, setSavedNotes] = useState(() => {
    const saved = localStorage.getItem("cd_student_lesson_notes");
    return saved ? JSON.parse(saved) : {};
  });

  const studentName = currentUser?.name || "Creator";
  const studentAvatar =
    currentUser?.avatar ||
    "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=200&q=80";

  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [isLoadingEnrolled, setIsLoadingEnrolled] = useState(
    Boolean(currentUser && localStorage.getItem("cd_user_token")),
  );
  const [enrolledLoadError, setEnrolledLoadError] = useState("");

  useEffect(() => {
    if (!currentUser || !localStorage.getItem("cd_user_token")) {
      setIsLoadingEnrolled(false);
      return;
    }

    setIsLoadingEnrolled(true);
    setEnrolledLoadError("");
    getLearningDashboard()
      .then((response) => {
        if (Array.isArray(response?.courses)) {
          setEnrolledCourses(response.courses);
        }
      })
      .catch((error) => {
        console.error("Unable to load learning dashboard:", error);
        setEnrolledLoadError("Unable to load your enrolled courses.");
      })
      .finally(() => setIsLoadingEnrolled(false));
  }, [currentUser]);

  const [popularRecommendedCourses, setPopularRecommendedCourses] = useState([]);
  const [isLoadingRecommendations, setIsLoadingRecommendations] = useState(true);
  const [recommendationsLoadError, setRecommendationsLoadError] = useState("");

  useEffect(() => {
    getCourses()
      .then((courses) => {
        if (!Array.isArray(courses)) return;

        setPopularRecommendedCourses(
          courses.map((course) => ({
            id: String(course.id),
            courseType: course.courseType,
            title: course.title,
            instructor: course.instructor || "Creator Dost Mentor",
            badge: course.courseType || "Creator Program",
            price: course.totalPrice ? `₹${course.totalPrice}` : "View details",
            originalPrice: course.price ? `₹${course.price}` : "",
            rating: course.rating || "New",
            reviewsCount: course.reviewCount || 0,
            thumbnail: course.imageUrl,
            highlights: String(course.details || "Practical creator skills")
              .split(/[.!?]/)
              .map((item) => item.trim())
              .filter(Boolean)
              .slice(0, 3),
          })),
        );
      })
      .catch((error) => {
        console.error("Unable to load recommended courses:", error);
        setRecommendationsLoadError("Unable to load recommended courses.");
      })
      .finally(() => setIsLoadingRecommendations(false));
  }, []);

  // Sync lesson notes when active lecture changes
  useEffect(() => {
    if (activeCoursePlayer) {
      const currentLectureId = activeCoursePlayer.lectures[activeLectureIndex]?.id;
      const noteKey = `${activeCoursePlayer.id}_lec_${currentLectureId}`;
      setLessonNoteText(savedNotes[noteKey] || "");
    }
  }, [activeCoursePlayer, activeLectureIndex, savedNotes]);

  const handleSaveNote = () => {
    if (!activeCoursePlayer) return;
    const currentLectureId = activeCoursePlayer.lectures[activeLectureIndex]?.id;
    const noteKey = `${activeCoursePlayer.id}_lec_${currentLectureId}`;
    const updated = { ...savedNotes, [noteKey]: lessonNoteText };
    setSavedNotes(updated);
    localStorage.setItem("cd_student_lesson_notes", JSON.stringify(updated));
    alert("📝 Note saved for this lesson!");
  };

  const handleContinueLearning = async (course) => {
    try {
      const response = await getLearningCourse(course.id);
      const loadedCourse = response.course;
      const nextLectureIndex = loadedCourse.lectures.findIndex((lecture) => !lecture.isCompleted);
      setActiveCoursePlayer(loadedCourse);
      setActiveLectureIndex(nextLectureIndex >= 0 ? nextLectureIndex : 0);
    } catch (error) {
      console.error("Unable to continue learning:", error);
    }
  };

  const handleMarkComplete = async () => {
    if (!activeCoursePlayer) return;

    const currentLecture = activeCoursePlayer.lectures[activeLectureIndex];
    if (!currentLecture) return;

    try {
      await updateLearningProgress(activeCoursePlayer.id, currentLecture.id, true);
    } catch (error) {
      console.error("Unable to save learning progress:", error);
      return;
    }

    const updatedCourses = enrolledCourses.map((c) => {
      if (c.id === activeCoursePlayer.id) {
        const updatedLectures = [...c.lectures];
        updatedLectures[activeLectureIndex] = {
          ...updatedLectures[activeLectureIndex],
          isCompleted: true
        };
        const completedCount = updatedLectures.filter((l) => l.isCompleted).length;
        const newProgress = Math.round((completedCount / updatedLectures.length) * 100);
        return {
          ...c,
          lectures: updatedLectures,
          completedLectures: completedCount,
          progress: newProgress
        };
      }
      return c;
    });

    setEnrolledCourses(updatedCourses);
    const refreshedActiveCourse = updatedCourses.find((c) => c.id === activeCoursePlayer.id);
    setActiveCoursePlayer(refreshedActiveCourse);
  };

  const filteredEnrolled = enrolledCourses.filter((course) => {
    const progress = Number.parseFloat(
      String(course.progress ?? 0).replace("%", "").trim(),
    ) || 0;
    const completedLectures = Number(course.completedLectures) || 0;

    if (activeTab === "in-progress") {
      return (progress > 0 || completedLectures > 0) && progress < 100;
    }
    if (activeTab === "completed") return progress === 100;
    return true;
  });

  const completedCount = enrolledCourses.filter((c) => c.progress === 100).length;

  return (
    <div className="ml-dashboard-wrapper">
      {/* 1. HERO WORKSPACE BANNER */}
      <section className="ml-header-banner">
        <div className="ml-max-width ml-header-flex">
          <div className="ml-user-profile-summary">
            <div className="ml-avatar-box">
              <img src={studentAvatar} alt="Student Profile" />
              <span className="ml-avatar-online-dot"></span>
            </div>
            <div className="ml-user-meta">
              <span className="ml-welcome-kicker">STUDENT DASHBOARD</span>
              <h2>Welcome back, {studentName}! 👋</h2>
              <p>Pick up where you left off, review your lessons, and download certificates.</p>
            </div>
          </div>

          {/* STATS RIBBON */}
          <div className="ml-header-stats-ribbon">
            <div className="ml-stat-card glow-blue">
              <span className="ml-stat-icon">📦</span>
              <div>
                <h4>{enrolledCourses.length}</h4>
                <p>Enrolled Courses</p>
              </div>
            </div>
            <div className="ml-stat-card glow-cyan">
              <span className="ml-stat-icon">⏱️</span>
              <div>
                <h4>14h 25m</h4>
                <p>Watch Time</p>
              </div>
            </div>
            <div className="ml-stat-card glow-gold">
              <span className="ml-stat-icon">🏆</span>
              <div>
                <h4>{completedCount}</h4>
                <p>Certificates Earned</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. MAIN ENROLLED COURSES CONTENT */}
      <section className="ml-max-width ml-main-content">
        <div className="ml-section-header">
          <div>
            <span className="ml-section-kicker">YOUR CURRICULUM</span>
            <h2>My Enrolled Courses</h2>
            <p>Access your active programs, resume playback, and download course assets.</p>
          </div>

          <div className="ml-tab-filters">
            <button
              className={`ml-filter-btn ${activeTab === "all" ? "active" : ""}`}
              onClick={() => setActiveTab("all")}
            >
              All Courses ({enrolledCourses.length})
            </button>
            <button
              className={`ml-filter-btn ${activeTab === "in-progress" ? "active" : ""}`}
              onClick={() => setActiveTab("in-progress")}
            >
              In Progress
            </button>
            <button
              className={`ml-filter-btn ${activeTab === "completed" ? "active" : ""}`}
              onClick={() => setActiveTab("completed")}
            >
              Completed 🎓
            </button>
          </div>
        </div>

        {/* ENROLLED CARDS GRID */}
        <div className="ml-enrolled-grid">
          {isLoadingEnrolled || enrolledLoadError || filteredEnrolled.length === 0 ? (
            <ApiContentState
              loading={isLoadingEnrolled}
              error={enrolledLoadError}
              emptyMessage={
                enrolledCourses.length === 0
                  ? "You have no enrolled courses yet."
                  : "No courses match this filter."
              }
            />
          ) : filteredEnrolled.map((course) => (
            <div key={course.id} className="ml-enrolled-card">
              <div className="ml-card-thumb">
                <img src={course.thumbnail} alt={course.title} />
                <span className="ml-category-tag">{course.category}</span>
                {course.progress === 100 && (
                  <span className="ml-completed-badge">✓ Completed</span>
                )}
                <div
                  className="ml-card-hover-play"
                  onClick={() => handleContinueLearning(course)}
                >
                  <span className="ml-play-trigger-orb">▶ Resume Course</span>
                </div>
              </div>

              <div className="ml-card-body">
                <span className="ml-instructor-name">Lead Mentor: {course.instructor}</span>
                <h3 className="ml-course-title">{course.title}</h3>

                {/* PROGRESS CONTAINER */}
                <div className="ml-progress-container">
                  <div className="ml-progress-text-row">
                    <span>Course Progress</span>
                    <strong>{course.progress}% Completed</strong>
                  </div>
                  <div className="ml-progress-track-bg">
                    <div
                      className="ml-progress-fill"
                      style={{ width: `${course.progress}%` }}
                    />
                  </div>
                  <span className="ml-lectures-counter">
                    {course.completedLectures} of {course.totalLectures} Lessons Finished
                  </span>
                </div>

                {/* ACTION BUTTONS */}
                <div className="ml-card-actions">
                  <button
                    className="ml-btn-continue"
                    onClick={() => handleContinueLearning(course)}
                  >
                    {course.progress === 100 ? "Revisit Lessons 🔁" : "Continue Learning ➔"}
                  </button>

                  {course.progress === 100 && (
                    <button
                      className="ml-btn-certificate"
                      onClick={() => setActiveCertificateCourse(course)}
                    >
                      📜 View Certificate
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 3. CROSS-SELLING: POPULAR MASTERCLASSES */}
        <div className="ml-popular-section-container">
          <div className="ml-popular-header">
            <div>
              <span className="ml-popular-kicker">LEVEL UP NEXT</span>
              <h2>Expand Your Creator Toolkit</h2>
              <p>Top-rated masterclasses enrolled by creators to grow reach and monetization.</p>
            </div>
            <button
              className="ml-btn-view-all-courses"
              onClick={() => (window.location.hash = "#courses")}
            >
              Browse Catalog ➔
            </button>
          </div>

          <div className="ml-popular-courses-grid">
            {isLoadingRecommendations || recommendationsLoadError || popularRecommendedCourses.length === 0 ? (
              <ApiContentState
                loading={isLoadingRecommendations}
                error={recommendationsLoadError}
                emptyMessage="No recommended courses are available right now."
              />
            ) : popularRecommendedCourses.map((popCourse) => (
              <div key={popCourse.id} className="ml-popular-card">
                <div className="ml-popular-thumb">
                  <img src={popCourse.thumbnail} alt={popCourse.title} />
                  <span className="ml-pop-badge">{popCourse.badge}</span>
                </div>

                <div className="ml-popular-body">
                  <div className="ml-pop-rating-row">
                    <span className="ml-stars">★ {popCourse.rating}</span>
                    <span className="ml-reviews">({popCourse.reviewsCount} reviews)</span>
                  </div>

                  <h3 className="ml-pop-title">{popCourse.title}</h3>
                  <span className="ml-pop-instructor">By {popCourse.instructor}</span>

                  <ul className="ml-pop-highlights-list">
                    {popCourse.highlights.map((point, idx) => (
                      <li key={idx}>✓ {point}</li>
                    ))}
                  </ul>

                  <div className="ml-pop-footer-row">
                    <div className="ml-pop-price-box">
                      <span className="ml-current-price">{popCourse.price}</span>
                      <del className="ml-old-price">{popCourse.originalPrice}</del>
                    </div>

                    <button
                      className="ml-btn-enroll-pop"
                      onClick={() => {
                        if (onSelectCourse) {
                          onSelectCourse(popCourse);
                        } else {
                          const courseSlug = courseTitleToSlug(popCourse.title);
                          window.location.hash = `#course-details?course=${encodeURIComponent(courseSlug)}`;
                        }
                      }}
                    >
                      Join Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===========================================================================
          MODALS SECTION
      =========================================================================== */}

      {/* MODAL 1: VIDEO LESSON PLAYER WORKSPACE */}
      {activeCoursePlayer && (
        <div className="ml-player-fixed-modal">
          <div
            className="ml-player-backdrop"
            onClick={() => setActiveCoursePlayer(null)}
          />

          <div className="ml-player-modal-body">
            <div className="ml-player-top-bar">
              <div className="ml-player-title-box">
                <span className="ml-now-playing-tag">NOW PLAYING</span>
                <h3>{activeCoursePlayer.title}</h3>
                <p>
                  Lesson {activeLectureIndex + 1}:{" "}
                  {activeCoursePlayer.lectures[activeLectureIndex]?.title}
                </p>
              </div>
              <button
                className="ml-player-close-btn"
                onClick={() => setActiveCoursePlayer(null)}
              >
                ✕ Close Workspace
              </button>
            </div>

            <div className="ml-player-workspace-grid">
              <div className="ml-player-left-video">
                <YouTubeLecturePlayer
                  key={activeCoursePlayer.lectures[activeLectureIndex]?.id}
                  lectureId={activeCoursePlayer.lectures[activeLectureIndex]?.id}
                  title={activeCoursePlayer.lectures[activeLectureIndex]?.title}
                />

                <div className="ml-video-bottom-controls">
                  <div className="ml-prev-next-group">
                    <button
                      className="ml-btn-nav-lec"
                      disabled={activeLectureIndex === 0}
                      onClick={() => setActiveLectureIndex((prev) => Math.max(prev - 1, 0))}
                    >
                      ⏮ Prev Lesson
                    </button>
                    <button
                      className="ml-btn-nav-lec"
                      disabled={
                        activeLectureIndex === activeCoursePlayer.lectures.length - 1
                      }
                      onClick={() =>
                        setActiveLectureIndex((prev) =>
                          Math.min(prev + 1, activeCoursePlayer.lectures.length - 1)
                        )
                      }
                    >
                      Next Lesson ⏭
                    </button>
                  </div>

                  <button
                    className={`ml-btn-mark-complete ${
                      activeCoursePlayer.lectures[activeLectureIndex]?.isCompleted
                        ? "completed-state"
                        : ""
                    }`}
                    onClick={handleMarkComplete}
                  >
                    {activeCoursePlayer.lectures[activeLectureIndex]?.isCompleted
                      ? "✓ Completed"
                      : "Mark as Complete"}
                  </button>
                </div>

                <div className="ml-lesson-companion-box">
                  <div className="ml-companion-tabs-nav">
                    <button
                      className={`ml-companion-tab-btn ${
                        playerTab === "resources" ? "active" : ""
                      }`}
                      onClick={() => setPlayerTab("resources")}
                    >
                      📁 Downloadable Resources ({activeCoursePlayer.resources.length})
                    </button>
                    <button
                      className={`ml-companion-tab-btn ${
                        playerTab === "notes" ? "active" : ""
                      }`}
                      onClick={() => setPlayerTab("notes")}
                    >
                      📝 My Lesson Notes
                    </button>
                  </div>

                  <div className="ml-companion-tab-pane">
                    {playerTab === "resources" && (
                      <div className="ml-resources-list">
                        {activeCoursePlayer.resources.map((res, i) => (
                          <div key={i} className="ml-resource-item">
                            <div>
                              <h5>{res.name}</h5>
                              <span>File Size: {res.size}</span>
                            </div>
                            <button
                              className="ml-btn-download-res"
                              onClick={() => alert(`📥 Downloading ${res.name}...`)}
                            >
                              Download ⬇
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {playerTab === "notes" && (
                      <div className="ml-notes-notepad">
                        <textarea
                          placeholder="Type your notes, timestamps, or ideas for this specific lesson..."
                          value={lessonNoteText}
                          onChange={(e) => setLessonNoteText(e.target.value)}
                        />
                        <button className="ml-btn-save-note" onClick={handleSaveNote}>
                          💾 Save Notes
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="ml-player-right-playlist">
                <h4>
                  Course Lessons ({activeCoursePlayer.completedLectures}/
                  {activeCoursePlayer.lectures.length})
                </h4>

                <div className="ml-playlist-items-stack">
                  {activeCoursePlayer.lectures.map((lec, idx) => (
                    <div
                      key={idx}
                      className={`ml-playlist-item ${
                        activeLectureIndex === idx ? "playing-now" : ""
                      } ${lec.isCompleted ? "is-finished" : ""}`}
                      onClick={() => setActiveLectureIndex(idx)}
                    >
                      <span className="ml-play-icon-indicator">
                        {lec.isCompleted
                          ? "✓"
                          : activeLectureIndex === idx
                          ? "▶"
                          : "🎥"}
                      </span>
                      <div className="ml-playlist-item-meta">
                        <h5>{lec.title}</h5>
                        <span>{lec.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 2: VERIFIED CERTIFICATE PREVIEW MODAL */}
      {activeCertificateCourse && (
        <div className="ml-cert-modal-curtain">
          <div
            className="ml-player-backdrop"
            onClick={() => setActiveCertificateCourse(null)}
          />
          <div className="ml-cert-modal-body">
            <div className="ml-cert-modal-header">
              <h3>Verified Student Certificate 🎓</h3>
              <button
                className="ml-player-close-btn"
                onClick={() => setActiveCertificateCourse(null)}
              >
                ✕ Close
              </button>
            </div>

            <div className="ml-certificate-canvas">
              <div className="ml-cert-inner-border">
                <div className="ml-cert-brand-row">
                  <div className="ml-cert-logo">
                    Creators<span>Dost</span>
                  </div>
                  <span className="ml-cert-badge-gold">VERIFIED CREDENTIAL</span>
                </div>

                <p className="ml-cert-kicker">CERTIFICATE OF COMPLETION</p>
                <h1 className="ml-cert-student-name">{studentName}</h1>
                <p className="ml-cert-course-text">
                  has successfully mastered all modules and project submissions in the accredited program:
                </p>
                <h2 className="ml-cert-course-title">
                  {activeCertificateCourse.title}
                </h2>

                <div className="ml-cert-footer-row">
                  <div>
                    <span className="ml-cert-sign">Graduate FF</span>
                    <p>Lead Instructor & Mentor</p>
                  </div>
                  <div className="ml-cert-id-block">
                    <p>
                      Credential ID:{" "}
                      <strong>{activeCertificateCourse.credentialId}</strong>
                    </p>
                    <p>Issued: {activeCertificateCourse.completionDate}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="ml-cert-actions-bar">
              <button
                className="ml-btn-download-cert"
                onClick={() => alert("🎉 Downloading high-resolution PDF certificate...")}
              >
                📥 Download PDF Certificate
              </button>
              <button
                className="ml-btn-linkedin-share"
                onClick={() =>
                  window.open("https://www.linkedin.com/", "_blank")
                }
              >
                💼 Share on LinkedIn
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}